import { useEffect, useState, useCallback, useMemo } from 'react'
import { usePortfolioManagerStore } from '../store/portfolioManagerStore'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const CATEGORY_BADGES: Record<string, { emoji: string; label: string; color: string }> = {
  immediate: { emoji: '🔴', label: 'Immediate', color: 'bg-red-100 text-red-800' },
  monthly: { emoji: '📅', label: 'Monthly', color: 'bg-blue-100 text-blue-800' },
  upcoming: { emoji: '📆', label: 'Upcoming', color: 'bg-purple-100 text-purple-800' },
  leaps: { emoji: '📈', label: 'LEAPs', color: 'bg-green-100 text-green-800' },
  pmcc: { emoji: '🔄', label: 'PMCC', color: 'bg-orange-100 text-orange-800' },
  action: { emoji: '✅', label: 'Action', color: 'bg-emerald-100 text-emerald-800' },
}

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800' },
  dismissed: { label: 'Dismissed', color: 'bg-red-100 text-red-700' },
  tracking: { label: 'Tracking', color: 'bg-blue-100 text-blue-800' },
}

export default function RecommendationsPage() {
  const {
    reportSections,
    sectionActions,
    availableReports,
    currentReportId,
    currentReportTitle,
    currentReportDate,
    loading,
    error,
    fetchReportSections,
    fetchSectionActions,
    updateSectionAction,
    fetchAvailableReports,
  } = usePortfolioManagerStore()

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [selectedReportId, setSelectedReportId] = useState<string>('')

  // Load data on mount
  useEffect(() => {
    fetchAvailableReports()
    fetchReportSections()
    fetchSectionActions()
  }, [fetchAvailableReports, fetchReportSections, fetchSectionActions])

  // Expand all sections by default when they load
  useEffect(() => {
    if (reportSections.length > 0) {
      setExpandedSections(new Set(reportSections.map(s => s.id)))
    }
  }, [reportSections])

  const handleReportChange = useCallback((reportId: string) => {
    setSelectedReportId(reportId)
    fetchReportSections(reportId || undefined)
    fetchSectionActions()
  }, [fetchReportSections, fetchSectionActions])

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }, [])

  const getSectionStatus = useCallback((sectionId: string): string => {
    if (!currentReportId) return 'pending'
    const key = `${currentReportId}__${sectionId}`
    return sectionActions[key] || 'pending'
  }, [currentReportId, sectionActions])

  const handleAction = useCallback((sectionId: string, status: string) => {
    if (!currentReportId) return
    const currentStatus = getSectionStatus(sectionId)
    // Toggle: click again to revert to pending
    const newStatus = currentStatus === status ? 'pending' : status
    updateSectionAction(currentReportId, sectionId, newStatus)
  }, [currentReportId, getSectionStatus, updateSectionAction])

  // Summary bar counts
  const summary = useMemo(() => {
    const counts = { total: reportSections.length, pending: 0, accepted: 0, dismissed: 0, tracking: 0 }
    for (const section of reportSections) {
      const status = getSectionStatus(section.id)
      if (status in counts) {
        counts[status as keyof typeof counts]++
      }
    }
    counts.pending = counts.total - counts.accepted - counts.dismissed - counts.tracking
    return counts
  }, [reportSections, getSectionStatus])

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Recommendations</h1>
        <p className="text-sm text-[var(--color-text-muted)]">
          AI-generated recommendation sections extracted from your weekly portfolio review reports
        </p>
      </div>

      {/* Report Selector */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-[var(--color-text-muted)]">Report:</label>
        <select
          value={selectedReportId}
          onChange={(e) => handleReportChange(e.target.value)}
          className="text-sm px-3 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] min-w-[300px]"
        >
          <option value="">Latest Report</option>
          {availableReports.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title} — {new Date(r.created_at).toLocaleDateString()}
            </option>
          ))}
        </select>
        {currentReportTitle && (
          <span className="text-xs text-[var(--color-text-muted)]">
            Viewing: <strong>{currentReportTitle}</strong>
            {currentReportDate && ` (${new Date(currentReportDate).toLocaleDateString()})`}
          </span>
        )}
      </div>

      {/* Summary Bar */}
      {reportSections.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-sm text-[var(--color-text-muted)] flex items-center gap-4">
          <span className="font-medium">{summary.total} sections found</span>
          <span>—</span>
          <span>{summary.pending} pending</span>
          <span className="text-green-600">{summary.accepted} accepted</span>
          <span className="text-red-600">{summary.dismissed} dismissed</span>
          <span className="text-blue-600">{summary.tracking} tracking</span>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && reportSections.length === 0 && (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <p>Loading report sections...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && reportSections.length === 0 && (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <p className="text-lg mb-2">No recommendation sections found</p>
          <p className="text-sm">
            Run a weekly portfolio review in AI Chat to generate recommendations.
            The report should contain sections like "Immediate Weekly Recommendations",
            "LEAP Calls Opportunities", etc.
          </p>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        {reportSections.map((section) => {
          const isExpanded = expandedSections.has(section.id)
          const status = getSectionStatus(section.id)
          const badge = CATEGORY_BADGES[section.category] || { emoji: '📋', label: section.category, color: 'bg-gray-100 text-gray-800' }
          const statusBadge = STATUS_BADGES[status] || STATUS_BADGES.pending

          return (
            <div
              key={section.id}
              className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-bg-primary)]"
            >
              {/* Section Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-bg-secondary)]">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Chevron */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors flex-shrink-0"
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>

                  {/* Category badge */}
                  <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${badge.color}`}>
                    {badge.emoji} {badge.label}
                  </span>

                  {/* Heading */}
                  <span
                    className="font-medium text-[var(--color-text-primary)] truncate cursor-pointer"
                    onClick={() => toggleSection(section.id)}
                  >
                    {section.heading}
                  </span>

                  {/* Status badge */}
                  {status !== 'pending' && (
                    <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${statusBadge.color}`}>
                      {statusBadge.label}
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                  <button
                    onClick={() => handleAction(section.id, 'accepted')}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      status === 'accepted'
                        ? 'bg-green-600 text-white'
                        : 'text-green-600 hover:bg-green-50 border border-green-200'
                    }`}
                    title="Accept"
                  >
                    ✓ Accept
                  </button>
                  <button
                    onClick={() => handleAction(section.id, 'dismissed')}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      status === 'dismissed'
                        ? 'bg-red-600 text-white'
                        : 'text-red-600 hover:bg-red-50 border border-red-200'
                    }`}
                    title="Dismiss"
                  >
                    ✕ Dismiss
                  </button>
                  <button
                    onClick={() => handleAction(section.id, 'tracking')}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      status === 'tracking'
                        ? 'bg-blue-600 text-white'
                        : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                    }`}
                    title="Track"
                  >
                    👁 Track
                  </button>
                </div>
              </div>

              {/* Section Content */}
              {isExpanded && (
                <div className="px-4 py-4 report-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {section.content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
